import type { DateType } from '@date-io/type'
import { ChevronLeft, ChevronRight } from '@patrianna-icons/material-icons'
import clsx from 'classnames'
import * as React from 'react'

import { useIsLandscape } from '../_shared/hooks/useIsLandscape'
import { useUtils } from '../_shared/hooks/useUtils'
import { useViews } from '../_shared/hooks/useViews'
import { datePickerDefaultProps } from '../constants/prop-types'
import type { BasePickerProps } from '../typings/BasePicker'
import type { MaterialUiPickersDate } from '../typings/date'
import Calendar from '../views/Calendar/Calendar'
import { YearSelection } from '../views/Year/YearView'

const viewsMap = {
  year: YearSelection,
  date: Calendar,
}
const defaultViews = Object.keys(viewsMap) as PickerView[]

export type PickerView = keyof typeof viewsMap

export type ToolbarComponentProps = {
  views: PickerView[]
  openView: PickerView
  date: MaterialUiPickersDate
  setOpenView: (view: PickerView) => void
  onChange: (date: MaterialUiPickersDate, isFinish?: boolean) => void
  // TODO move out, cause it is DateTimePickerOnly
  hideTabs?: boolean
  dateRangeIcon?: React.ReactNode
  timeIcon?: React.ReactNode
  isLandscape: boolean
}

export interface PickerViewProps {
  views: PickerView[]
  openTo: PickerView
  disableToolbar?: boolean
  ToolbarComponent: React.ComponentType<ToolbarComponentProps>
  // TODO move out, cause it is DateTimePickerOnly
  hideTabs?: boolean
  dateRangeIcon?: React.ReactNode
  timeIcon?: React.ReactNode
}

interface PickerProps extends PickerViewProps {
  date: MaterialUiPickersDate
  orientation?: BasePickerProps['orientation']
  onChange: (date: MaterialUiPickersDate, isFinish?: boolean) => void
  minDate?: DateType
  maxDate?: DateType
}

export const Picker: React.FunctionComponent<PickerProps> = ({
  date,
  views = defaultViews,
  disableToolbar,
  onChange,
  openTo,
  minDate: unparsedMinDate = datePickerDefaultProps.minDate,
  maxDate: unparsedMaxDate = datePickerDefaultProps.maxDate,
  ToolbarComponent,
  orientation,
  ...rest
}) => {
  const utils = useUtils()
  const isLandscape = useIsLandscape(orientation)
  const { openView, setOpenView, handleChangeAndOpenNext } = useViews(views, openTo, onChange)

  const minDate = React.useMemo(() => utils.date(unparsedMinDate)!, [unparsedMinDate, utils])
  const maxDate = React.useMemo(() => utils.date(unparsedMaxDate)!, [unparsedMaxDate, utils])

  return (
    <div
      className={clsx('MuiPickersBasePicker-container', {
        'MuiPickersBasePicker-containerLandscape': isLandscape,
      })}
    >
      {!disableToolbar && (
        <ToolbarComponent
          {...rest}
          views={views}
          isLandscape={isLandscape}
          date={date}
          onChange={onChange}
          setOpenView={setOpenView}
          openView={openView}
        />
      )}

      <div
        className={clsx('MuiPickersBasePicker-pickerView', {
          'MuiPickersBasePicker-pickerViewLandscape': isLandscape,
        })}
      >
        {openView === 'date' && (
          <Calendar
            {...rest}
            date={date}
            onChange={handleChangeAndOpenNext}
            minDate={minDate}
            maxDate={maxDate}
            leftArrowIcon={<ChevronLeft />}
            rightArrowIcon={<ChevronRight />}
          />
        )}

        {openView === 'year' && (
          <YearSelection {...rest} date={date} onChange={handleChangeAndOpenNext} minDate={minDate} maxDate={maxDate} />
        )}
      </div>
    </div>
  )
}
